import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import { catchError, EMPTY, of, switchMap, tap } from 'rxjs';
import { ROOT_MAINTENANCE_COMPONENT } from 'src/app/core/constants';
import { ModuleNumbers } from 'src/app/core/enums/module-numbers.enum';
import {
  AdminMaintenanceRoutes,
  AdminRoutesEnum,
  UserMaintenanceRoutes,
} from 'src/app/core/enums/routes.enum';
import { CreateUpdateDeleteRecursiveArrayService } from 'src/app/core/services/create-update-delete-recursive-array.service';
import { ToastService } from 'src/app/core/services/toast.service';
import { LoadSpecificMenuStructure } from 'src/app/core/state/menu-tree-structure.actions';
import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import { IdName } from 'src/app/shared/models/id-name';
import { IdNameSfi } from 'src/app/shared/models/id-name-sfi.model';
import { ComponentTransferRolesResponse } from 'src/app/shared/models/component-transfer-roles.model';
import {
  ComponentCopyStatus,
  CopyComponentsModel,
  IComponentsStructureMenu,
} from '../models/components-structure-menu.model';
import { IMaintenanceComponentGroup } from '../models/maintenance-component-group.model';
import { IMaintenanceComponent } from '../models/maintenance-component.model';
import { ComponentStructureApiService } from '../services/component-structure.api.service';
import {
  ClearComponentDetails,
  ClearComponentGroupDetails,
  CopyComponentStructure,
  CreateMaintenanceComponent,
  CreateMaintenanceComponentGroup,
  DeleteMaintenanceComponent,
  DeleteMaintenanceComponentGroup,
  GetAllMaintenanceComponents,
  GetAllMaintenanceComponentsForTenant,
  GetComponentCopyStatus,
  GetMaintenanceComponentById,
  GetMaintenanceComponentGroupById,
  GetMaintenanceComponentGroups,
  GetMaintenanceComponents,
  GetMaintenanceComponentsForDepartment,
  GetMaintenanceMenuStructure,
  GetComponentTransferRoles,
  LoadComponentsForIO,
  PrintComponentList,
  TransformToComponent,
  TransformToComponentsGroup,
  TransferMaintenanceComponent,
  UpdateComponentsStructureOrder,
  UpdateMaintenanceComponent,
  UpdateMaintenanceComponentGroup,
  GetComponentsListForDepartmentSearchable,
  LoadMoreComponentsListForDepartmentSearchable,
} from './components-structure.actions';

export interface ComponentsStructureStateModel {
  componentsMenu: IComponentsStructureMenu[];
  componentsForDepartment: IMaintenanceComponent[];
  componentsForDepartmentWithSFI: IMaintenanceComponent[];
  componentListForTenant: IdNameSfi[];
  componentListForDepartment: IdName[];
  allComponentsListForDepartment: IdNameSfi[];
  componentsListForDepartmentSearchable: IdNameSfi[];
  componentsListForDepartmentSearchableCurrentPage: number;
  componentsListForDepartmentSearchableTotalPages: number;
  componentsListForDepartmentSearchableSearchPhrase: string | undefined;
  componentGroupList: IdName[];
  componentDetailed?: IMaintenanceComponent;
  componentGroupDetailed?: IMaintenanceComponentGroup;
  currentPrintedList?: Blob;
  componentsForIO: IdName[];
  componentCopyStatus: ComponentCopyStatus;
  componentTransferRoles?: ComponentTransferRolesResponse;
}

const COMPONENTS_STRUCTURE = new StateToken<ComponentsStructureStateModel>('componentsStructure');

@State<ComponentsStructureStateModel>({
  name: COMPONENTS_STRUCTURE,
  defaults: {
    allComponentsListForDepartment: [],
    componentsListForDepartmentSearchable: [],
    componentsListForDepartmentSearchableCurrentPage: 0,
    componentsListForDepartmentSearchableTotalPages: 0,
    componentsListForDepartmentSearchableSearchPhrase: undefined,
    componentsMenu: [],
    componentsForDepartment: [ROOT_MAINTENANCE_COMPONENT],
    componentsForDepartmentWithSFI: [ROOT_MAINTENANCE_COMPONENT],
    componentGroupList: [ROOT_MAINTENANCE_COMPONENT],
    componentListForTenant: [],
    componentListForDepartment: [],
    componentDetailed: undefined,
    componentGroupDetailed: undefined,
    currentPrintedList: undefined,
    componentsForIO: [],
    componentCopyStatus: {
      pendingCount: 0,
      failedCount: 0,
    },
    componentTransferRoles: undefined,
  },
})
@Injectable()
export class ComponentsStructureState {
  private readonly toast = inject(ToastService);

  constructor(
    private service: ComponentStructureApiService,
    private router: Router
  ) {}

  private get isInAdmin(): boolean {
    return this.router.url.includes(AdminRoutesEnum.Admin);
  }

  // MAINTENANCE COMPONENTS MENU
  // _______________________________________________________________________
  @Action(GetMaintenanceMenuStructure, { cancelUncompleted: true })
  getMaintenanceMenuStructure({ setState }: StateContext<ComponentsStructureStateModel>) {
    return this.service.loadComponentsMenuStructure().pipe(
      tap((componentsMenu: IComponentsStructureMenu[]) => {
        setState(
          patch({
            componentsMenu,
          })
        );
      })
    );
  }

  // MAINTENANCE COMPONENTS
  // _______________________________________________________________________
  @Action(CreateMaintenanceComponent, { cancelUncompleted: true })
  @OnSuccess({ url: AdminMaintenanceRoutes.Components, message: 'componentCreated' })
  createMaintenanceComponent(
    { dispatch }: StateContext<ComponentsStructureStateModel>,
    { component }: CreateMaintenanceComponent
  ) {
    return this.service.createComponent(component).pipe(
      tap(() => {
        dispatch(new GetMaintenanceMenuStructure());
      })
    );
  }

  @Action(UpdateMaintenanceComponent, { cancelUncompleted: true })
  updateMaintenanceComponent(
    { dispatch, patchState }: StateContext<ComponentsStructureStateModel>,
    { component }: UpdateMaintenanceComponent
  ) {
    return this.service.updateComponent(component).pipe(
      tap(() => {
        patchState({ componentDetailed: undefined });
        dispatch(new GetMaintenanceMenuStructure());
        if (!this.isInAdmin) {
          dispatch(new LoadSpecificMenuStructure(ModuleNumbers.Maintenance));
        }
      }),
      switchMap(() => {
        if (this.isInAdmin) {
          this.toast.success('componentUpdated');
          this.router.navigate([AdminMaintenanceRoutes.Components]);
          return of(undefined);
        }
        return dispatch(new GetMaintenanceComponentById(component.id)).pipe(
          tap(() => this.toast.success('componentUpdated')),
          catchError(() => {
            this.toast.error('maintenanceDetailsSavedReloadFailed');
            return of(undefined);
          })
        );
      })
    );
  }

  @Action(DeleteMaintenanceComponent, { cancelUncompleted: true })
  @OnSuccess({ url: AdminMaintenanceRoutes.Components, message: 'componentDeleted' })
  deleteMaintenanceComponent(
    { dispatch }: StateContext<ComponentsStructureStateModel>,
    { id }: DeleteMaintenanceComponent
  ) {
    return this.service.deleteComponent(id).pipe(
      tap(() => {
        dispatch(new GetMaintenanceMenuStructure());
      })
    );
  }

  @Action(TransferMaintenanceComponent, { cancelUncompleted: true })
  @OnSuccess({ message: 'componentTransferred' })
  transferMaintenanceComponent(
    _: StateContext<ComponentsStructureStateModel>,
    { transfer }: TransferMaintenanceComponent
  ) {
    return this.service.transferComponent(transfer);
  }

  @Action(GetMaintenanceComponentById, { cancelUncompleted: true })
  getMaintenanceComponentById(
    { setState }: StateContext<ComponentsStructureStateModel>,
    { id }: GetMaintenanceComponentById
  ) {
    return this.service.getComponentById(id).pipe(
      tap((componentDetailed: IMaintenanceComponent) => {
        setState(
          patch({
            componentDetailed,
          })
        );
      })
    );
  }

  @Action(GetMaintenanceComponents, { cancelUncompleted: true })
  getMaintenanceComponents({ setState }: StateContext<ComponentsStructureStateModel>) {
    return this.service.getComponentsForDepartment().pipe(
      tap((componentsForDepartment: IMaintenanceComponent[]) => {
        setState(
          patch({
            componentsForDepartment: [ROOT_MAINTENANCE_COMPONENT, ...componentsForDepartment],
            componentsForDepartmentWithSFI: [
              ROOT_MAINTENANCE_COMPONENT,
              ...componentsForDepartment
                .map((c) => ({
                  ...c,
                  name: `${c.sfiCode} ${c.name}`,
                }))
                .sort((a, b) => a.name.localeCompare(b.name)),
            ],
          })
        );
      })
    );
  }

  @Action(GetAllMaintenanceComponents, { cancelUncompleted: true })
  getAllMaintenanceComponents({ setState }: StateContext<ComponentsStructureStateModel>) {
    return this.service.getAllComponentListForDepartment().pipe(
      tap((allComponentsListForDepartment: IdNameSfi[]) => {
        setState(
          patch({
            allComponentsListForDepartment: [
              ROOT_MAINTENANCE_COMPONENT,
              ...allComponentsListForDepartment
                .map((c) => ({
                  ...c,
                  name: `${c.sfiCode} ${c.name}`,
                }))
                .sort((a, b) => a.name.localeCompare(b.name)),
            ],
          })
        );
      })
    );
  }

  @Action(GetComponentsListForDepartmentSearchable, { cancelUncompleted: true })
  getComponentsListForDepartmentSearchable(
    { setState }: StateContext<ComponentsStructureStateModel>,
    { searchPhrase }: GetComponentsListForDepartmentSearchable
  ) {
    return this.service.getComponentsListForDepartmentSearchable(searchPhrase, 0).pipe(
      tap(({ data, totalPages }) => {
        const mapped = data
          .map((c) => ({
            ...c,
            name: `${c.sfiCode} ${c.name}`,
          }))
          .sort((a, b) => a.name.localeCompare(b.name));
        setState(
          patch({
            componentsListForDepartmentSearchable: [ROOT_MAINTENANCE_COMPONENT, ...mapped],
            componentsListForDepartmentSearchableCurrentPage: 0,
            componentsListForDepartmentSearchableTotalPages: totalPages,
            componentsListForDepartmentSearchableSearchPhrase: searchPhrase,
          })
        );
      })
    );
  }

  @Action(LoadMoreComponentsListForDepartmentSearchable, { cancelUncompleted: true })
  loadMoreComponentsListForDepartmentSearchable({
    getState,
    setState,
  }: StateContext<ComponentsStructureStateModel>) {
    const state = getState();
    const searchPhrase = state.componentsListForDepartmentSearchableSearchPhrase;
    const nextPage = state.componentsListForDepartmentSearchableCurrentPage + 1;
    if (nextPage > state.componentsListForDepartmentSearchableTotalPages) {
      return EMPTY;
    }
    return this.service.getComponentsListForDepartmentSearchable(searchPhrase, nextPage).pipe(
      tap(({ data, totalPages }) => {
        const currentList = state.componentsListForDepartmentSearchable;
        const existingIds = new Set(currentList.map((c) => c.id));
        const mapped = data
          .map((c) => ({
            ...c,
            name: `${c.sfiCode} ${c.name}`,
          }))
          .filter((c) => !existingIds.has(c.id));
        const appended = [
          ROOT_MAINTENANCE_COMPONENT,
          ...currentList
            .filter((c) => c.id !== ROOT_MAINTENANCE_COMPONENT.id)
            .concat(mapped)
            .sort((a, b) => a.name.localeCompare(b.name)),
        ];
        setState(
          patch({
            componentsListForDepartmentSearchable: appended,
            componentsListForDepartmentSearchableCurrentPage: nextPage,
            componentsListForDepartmentSearchableTotalPages: totalPages,
          })
        );
      })
    );
  }

  @Action(GetAllMaintenanceComponentsForTenant, { cancelUncompleted: true })
  getAllMaintenanceComponentsForTenant({ setState }: StateContext<ComponentsStructureStateModel>) {
    return this.service.getComponentListForTenant().pipe(
      tap((response: IdName[]) => {
        setState(
          patch({
            componentListForTenant: response.map((r) => ({
              id: r.id,
              name: `${r.name.split(' ').slice(1).join(' ')}`,
              sfiCode: `${r.name.split(' ')[0]}`,
            })),
          })
        );
      })
    );
  }

  @Action(GetMaintenanceComponentsForDepartment, { cancelUncompleted: true })
  getMaintenanceComponentsForDepartment(
    { setState }: StateContext<ComponentsStructureStateModel>,
    { departmentId }: GetMaintenanceComponentsForDepartment
  ) {
    return this.service.getComponentListForDepartment(departmentId).pipe(
      tap((response: IdName[]) => {
        setState(
          patch({
            componentListForDepartment: response,
          })
        );
      })
    );
  }

  @Action(TransformToComponentsGroup, { cancelUncompleted: true })
  @OnSuccess({ message: 'componentTransformed' })
  transformToComponentsGroup(
    { dispatch }: StateContext<ComponentsStructureStateModel>,
    { id }: TransformToComponentsGroup
  ) {
    return this.service.transformToComponentsGroup(id).pipe(
      tap(() => {
        dispatch(new GetMaintenanceMenuStructure());
        if (this.isInAdmin) {
          this.router.navigate([AdminMaintenanceRoutes.Components]);
        } else {
          this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
            this.router.navigate([UserMaintenanceRoutes.Component, id]);
          });
        }
      })
    );
  }

  // MAINTENANCE COMPONENT GROUPS
  // _______________________________________________________________________
  @Action(CreateMaintenanceComponentGroup, { cancelUncompleted: true })
  @OnSuccess({ url: AdminMaintenanceRoutes.Components, message: 'componentGroupCreated' })
  createMaintenanceComponentGroup(
    { dispatch }: StateContext<ComponentsStructureStateModel>,
    { componentGroup }: CreateMaintenanceComponentGroup
  ) {
    return this.service.createComponentGroup(componentGroup).pipe(
      tap(() => {
        dispatch(new GetMaintenanceMenuStructure());
      })
    );
  }

  @Action(UpdateMaintenanceComponentGroup, { cancelUncompleted: true })
  updateMaintenanceComponentGroup(
    { dispatch, patchState }: StateContext<ComponentsStructureStateModel>,
    { componentGroup }: UpdateMaintenanceComponentGroup
  ) {
    return this.service.updateComponentGroup(componentGroup).pipe(
      tap(() => {
        patchState({ componentGroupDetailed: undefined });
        dispatch(new GetMaintenanceMenuStructure());
        if (!this.isInAdmin) {
          dispatch(new LoadSpecificMenuStructure(ModuleNumbers.Maintenance));
        }
      }),
      switchMap(() => {
        if (this.isInAdmin) {
          this.toast.success('componentGroupUpdated');
          this.router.navigate([AdminMaintenanceRoutes.Components]);
          return of(undefined);
        }
        return dispatch(new GetMaintenanceComponentGroupById(componentGroup.id)).pipe(
          tap(() => this.toast.success('componentGroupUpdated')),
          catchError(() => {
            this.toast.error('maintenanceDetailsSavedReloadFailed');
            return of(undefined);
          })
        );
      })
    );
  }

  @Action(DeleteMaintenanceComponentGroup, { cancelUncompleted: true })
  @OnSuccess({ url: AdminMaintenanceRoutes.Components, message: 'componentGroupDeleted' })
  deleteMaintenanceComponentGroup(
    { dispatch }: StateContext<ComponentsStructureStateModel>,
    { id }: DeleteMaintenanceComponentGroup
  ) {
    return this.service.deleteComponentGroup(id).pipe(
      tap(() => {
        dispatch(new GetMaintenanceMenuStructure());
      })
    );
  }

  @Action(TransformToComponent, { cancelUncompleted: true })
  @OnSuccess({ message: 'componentsGroupTransformed' })
  transformToComponent(
    { dispatch }: StateContext<ComponentsStructureStateModel>,
    { id }: TransformToComponent
  ) {
    return this.service.transformToComponent(id).pipe(
      tap(() => {
        dispatch(new GetMaintenanceMenuStructure());
        if (this.isInAdmin) {
          this.router.navigate([AdminMaintenanceRoutes.Components]);
        } else {
          this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
            this.router.navigate([UserMaintenanceRoutes.Component, id]);
          });
        }
      })
    );
  }

  @Action(GetMaintenanceComponentGroupById, { cancelUncompleted: true })
  getMaintenanceComponentGroupById(
    { setState }: StateContext<ComponentsStructureStateModel>,
    { id }: GetMaintenanceComponentGroupById
  ) {
    return this.service.getComponentGroupById(id).pipe(
      tap((componentGroupDetailed: IMaintenanceComponentGroup) => {
        setState(
          patch({
            componentGroupDetailed,
          })
        );
      })
    );
  }

  @Action(GetMaintenanceComponentGroups, { cancelUncompleted: true })
  getMaintenanceComponentGroups({ setState }: StateContext<ComponentsStructureStateModel>) {
    return this.service.getComponentGroupList().pipe(
      tap((componentGroupList: IMaintenanceComponentGroup[]) => {
        const compGroupIdNameList: IdName[] = [];
        compGroupIdNameList.push(ROOT_MAINTENANCE_COMPONENT);
        if (componentGroupList?.length > 0) {
          componentGroupList.forEach((c: IMaintenanceComponentGroup) => {
            compGroupIdNameList.push({ id: c.id, name: c.name });
          });
          setState(
            patch({
              componentGroupList: compGroupIdNameList,
            })
          );
        }
      })
    );
  }

  @Action(PrintComponentList, { cancelUncompleted: true })
  printMaintenanceJob(
    { setState }: StateContext<ComponentsStructureStateModel>,
    { request }: PrintComponentList
  ) {
    return this.service.printComponentsList(request).pipe(
      tap((file) => {
        setState(patch({ currentPrintedList: file }));
      })
    );
  }

  @Action(CopyComponentStructure, { cancelUncompleted: true })
  @OnSuccess({ message: 'componentsStructureCopiedSuccessfully' })
  copyComponentStructure(
    ctx: StateContext<CopyComponentsModel>,
    { model }: CopyComponentStructure
  ) {
    return this.service.copyComponentStructure(model);
  }

  @Action(GetComponentCopyStatus, { cancelUncompleted: true })
  getComponentCopyStatus({ setState }: StateContext<ComponentsStructureStateModel>) {
    return this.service.getComponentCopyStatus().pipe(
      tap((status: ComponentCopyStatus) => {
        setState(patch({ componentCopyStatus: status }));
      })
    );
  }

  @Action(UpdateComponentsStructureOrder, { cancelUncompleted: true })
  @OnSuccess({ message: 'componentsStructureOrderUpdated' })
  updateComponentsStructureOrder(
    { dispatch }: StateContext<ComponentsStructureStateModel>,
    { order }: UpdateComponentsStructureOrder
  ) {
    return this.service
      .updateComponentStructureOrder(order)
      .pipe
      // works flakey because the update operation is async on the server hence dispatching the action after the update is not guaranteed to work
      // tap(() => {
      // dispatch(new GetMaintenanceMenuStructure());
      // })
      ();
  }

  @Action(LoadComponentsForIO, { cancelUncompleted: true })
  loadComponentsForIO({ getState, setState }: StateContext<ComponentsStructureStateModel>) {
    return this.service.getComponentsForIO().pipe(
      tap((componentsForIO) => {
        const state = getState();
        setState({ ...state, componentsForIO });
      })
    );
  }

  @Action(ClearComponentDetails)
  clearComponentDetails({ setState }: StateContext<ComponentsStructureStateModel>) {
    setState(patch({ componentDetailed: undefined }));
  }

  @Action(ClearComponentGroupDetails)
  clearComponentGroupDetails({ setState }: StateContext<ComponentsStructureStateModel>) {
    setState(patch({ componentGroupDetailed: undefined }));
  }

  @Action(GetComponentTransferRoles, { cancelUncompleted: true })
  getComponentTransferRoles(
    { setState }: StateContext<ComponentsStructureStateModel>,
    { componentId }: GetComponentTransferRoles
  ) {
    return this.service.getComponentTransferRoles(componentId).pipe(
      tap((roles: ComponentTransferRolesResponse) => {
        setState(
          patch({
            componentTransferRoles: roles,
          })
        );
      })
    );
  }

  @Selector()
  static getComponentsStructure(state: ComponentsStructureStateModel) {
    return state.componentsMenu;
  }

  @Selector()
  static getFlatComponentsStructure(
    state: ComponentsStructureStateModel
  ): IComponentsStructureMenu[] {
    return CreateUpdateDeleteRecursiveArrayService.getFlatList(state.componentsMenu);
  }

  @Selector()
  static getComponentGroupList(state: ComponentsStructureStateModel) {
    return state.componentGroupList;
  }

  @Selector()
  static getComponentsForDepartment(state: ComponentsStructureStateModel) {
    return state.componentsForDepartment;
  }

  @Selector()
  static getComponentsForDepartmentWithSFI(state: ComponentsStructureStateModel) {
    return state.componentsForDepartmentWithSFI;
  }

  @Selector()
  static getAllComponentListForDepartment(state: ComponentsStructureStateModel) {
    return state.allComponentsListForDepartment;
  }

  @Selector()
  static getComponentsListForDepartmentSearchable(state: ComponentsStructureStateModel) {
    return state.componentsListForDepartmentSearchable;
  }

  @Selector()
  static getComponentsListForDepartmentSearchableHasMore(
    state: ComponentsStructureStateModel
  ): boolean {
    const searchPhrase = state.componentsListForDepartmentSearchableSearchPhrase;
    const currentPage = state.componentsListForDepartmentSearchableCurrentPage;
    const totalPages = state.componentsListForDepartmentSearchableTotalPages;
    return !searchPhrase && currentPage >= 0 && currentPage < totalPages - 1;
  }

  @Selector()
  static getAllComponentListForTenant(state: ComponentsStructureStateModel) {
    return state.componentListForTenant;
  }

  @Selector()
  static getComponentListForDepartment(state: ComponentsStructureStateModel) {
    return state.componentListForDepartment;
  }

  @Selector()
  static getComponentDetails(state: ComponentsStructureStateModel) {
    return state.componentDetailed;
  }

  @Selector()
  static getComponentGroupDetails(state: ComponentsStructureStateModel) {
    return state.componentGroupDetailed;
  }

  @Selector()
  static getPrintedList(state: ComponentsStructureStateModel) {
    return state.currentPrintedList;
  }

  @Selector()
  static getComponentsForIO(state: ComponentsStructureStateModel) {
    return state.componentsForIO;
  }

  @Selector()
  static getComponentCopyStatus(state: ComponentsStructureStateModel): ComponentCopyStatus {
    return state.componentCopyStatus;
  }

  @Selector()
  static getComponentTransferAccessRoles(state: ComponentsStructureStateModel): IdName[] {
    return state.componentTransferRoles?.access || [];
  }

  @Selector()
  static getComponentTransferResponsibleRoles(state: ComponentsStructureStateModel): IdName[] {
    return state.componentTransferRoles?.responsible || [];
  }
}
